import React, { useMemo, useState } from 'react';
import { PageHeader, DatePicker, Select, Input, Space, Table, Tag, Button, Avatar, Tooltip, Typography } from 'antd';
import { MailOutlined, PhoneOutlined, CalendarOutlined, LinkOutlined, LockOutlined, GlobalOutlined, CheckCircleOutlined, CloseCircleOutlined, SmileOutlined, RobotOutlined } from '@ant-design/icons';
import moment from 'moment';
import firebase from 'firebase/app';

import CenteredSpinner from '../../../components/CenteredSpinner';
import { CredentialHealthCard } from '../../../components/admin/CredentialHealthCard';
import { useUsers } from '../../../models/useUsers';
import { findUserDocInconsistencies } from '../../../utils/userConsistency';
import { getAdminDatePresets, getCurrentWeekRange } from '../../../utils/adminDatePresets';
import { getAppBaseUrl } from '../../../utils/URLBuilder';
import { downloadCsv } from '../../../utils/csv';

import '../../../App.scss';
import 'antd/dist/antd.css';

const isLocalHost = window.location.hostname === 'localhost';
const URLPath = getAppBaseUrl();
const { RangePicker } = DatePicker;
const { Text } = Typography;
const LIMIT = 400;

/** Sort weight for the Issues column: findings + the tombstone marker itself. */
const issueCount = (user) => findUserDocInconsistencies(user).length + (user.deleted === true ? 1 : 0);

function formatLoginMethod(authProvider) {
  const normalized = authProvider ? `${authProvider}`.toLowerCase() : null;
  if (!normalized) return 'Unknown';
  if (normalized === 'google') return 'Google';
  if (normalized === 'apple') return 'Apple';
  if (normalized === 'email') return 'Email';
  if (normalized === 'phone') return 'Phone';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function getPrimaryAuthProvider(user) {
  if (!user) return null;
  if (user.authProvider) return user.authProvider;
  if (Array.isArray(user.authProviders) && user.authProviders.length > 0) return user.authProviders[0];
  return null;
}

function getPrimaryOriginReason(user) {
  if (!user) return null;
  return user.signupOriginReason || user.originReason || user.lastOriginReason || null;
}

function getPrimaryOriginPath(user) {
  if (!user) return null;
  return user.signupOriginPath || user.originPath || user.lastOriginPath || null;
}

function toISOString(value) {
  if (!value) return '';
  if (value.toDate) return value.toDate().toISOString();
  if (value.toMillis) return new Date(value.toMillis()).toISOString();
  if (value instanceof Date) return value.toISOString();
  return '';
}


export default function AdminUsersScreen() {
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(getCurrentWeekRange);
  const [, setPresetRefresh] = useState(0);
  const [emailVerifiedMode, setEmailVerifiedMode] = useState('all');
  const [savingUserId, setSavingUserId] = useState(null);

  const startDate = dateRange && dateRange[0] ? dateRange[0].toDate() : null;
  const endDate = dateRange && dateRange[1] ? dateRange[1].toDate() : null;
  const emailVerifiedFilter = emailVerifiedMode === 'all'
    ? undefined
    : emailVerifiedMode === 'true';

  const [loading, users] = useUsers({
    startDate,
    endDate,
    emailVerifiedFilter,
    limit: LIMIT,
  });

  const filteredUsers = useMemo(() => {
    const list = users || [];
    const query = (searchText || '').toLowerCase().trim();
    if (!query) return list;

    return list.filter((user) => {
      const haystack = [
        user.id,
        user.uid,
        user.email,
        user.displayName,
        user.clientId,
        user.platform,
        user.authProvider,
        user.signupOriginReason,
        user.originReason,
        user.signupOriginPath,
        user.originPath,
        Array.isArray(user.authProviders) ? user.authProviders.join(' ') : null,
        `${!!user.emailVerified}`,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [users, searchText]);

  const resendVerificationEmail = async (userId, email) => {
    if (!userId || !email) return;
    setSavingUserId(userId);
    try {
      const resendEmailVerification = firebase
        .functions()
        .httpsCallable('userService-humanHandler-resendEmailVerification');
      const continueUrl = getAppBaseUrl();
      const result = await resendEmailVerification({ userId, continueUrl });
      if (result.data && result.data.verificationLink) {
        console.log('%c[EMULATOR] Verification link:', 'color: #ff0098; font-weight: bold;', result.data.verificationLink);
      }
    } catch (error) {
      console.error('[AdminUsersScreen] Failed to resend verification email:', error);
    } finally {
      setSavingUserId(null);
    }
  };

  const exportUsersCsv = () => {
    const rows = [
      [
        'userId',
        'uid',
        'displayName',
        'email',
        'photoURL',
        'createdAt',
        'updatedAt',
        'emailVerified',
        'isAnonymous',
        'authProvider',
        'authProviders',
        'loginMethod',
        'platform',
        'originReason',
        'originPath',
        'signupOriginReason',
        'signupOriginPath',
        'clientId',
      ],
      ...(filteredUsers || []).map((user) => [
        user.id || '',
        user.uid || user.id || '',
        user.displayName || '',
        user.email || '',
        user.photoURL || '',
        toISOString(user.createdAt),
        toISOString(user.updatedAt),
        !!user.emailVerified,
        !!user.isAnonymous,
        user.authProvider || '',
        Array.isArray(user.authProviders) ? user.authProviders.join(',') : (user.authProviders || ''),
        formatLoginMethod(getPrimaryAuthProvider(user)),
        user.platform || 'web',
        getPrimaryOriginReason(user) || '',
        getPrimaryOriginPath(user) || '',
        user.signupOriginReason || '',
        user.signupOriginPath || '',
        user.clientId || '',
      ]),
    ];
    const stamp = moment().format('YYYYMMDD-HHmmss');
    downloadCsv(`admin-users-${stamp}.csv`, rows);
  };

  const columns = [
    {
      // Doc-only findings, per row, zero extra reads — the "what am I looking
      // at?" companion to the credential-health sweep above. Client-dependent
      // checks stay on the detail screen where the client is actually loaded.
      title: 'Issues',
      key: 'issues',
      sorter: (a, b) => issueCount(b) - issueCount(a),
      render: (_, user) => {
        const findings = findUserDocInconsistencies(user);
        const tombstoned = user.deleted === true;
        if (!findings.length && !tombstoned) {
          return <Text type="secondary" style={{ fontSize: 12 }}>—</Text>;
        }
        return (
          <Space direction="vertical" size={2}>
            {tombstoned && (
              <Tooltip title="This person's sign-in was deleted (their data remains). If they own a live client, the restore panel is on their user page.">
                <Tag color="red">sign-in deleted</Tag>
              </Tooltip>
            )}
            {!user.clientId && !tombstoned && findings.length > 0 && (
              <Tag color="orange">no clientId</Tag>
            )}
            {findings.length > 0 && (
              <Tooltip
                title={(
                  <div>
                    {findings.map((f) => <div key={f} style={{ marginBottom: 4 }}>• {f}</div>)}
                  </div>
                )}
              >
                <Tag color="volcano">⚠ {findings.length} finding{findings.length === 1 ? '' : 's'}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Profile',
      key: 'profile',
      render: (_, user) => {
        const href = `${URLPath}/admin/users/${user.id}`;
        const createdAtLabel = user.createdAt && user.createdAt.toMillis
          ? moment(user.createdAt.toMillis()).format('YYYY-MM-DD HH:mm')
          : '-';
        return (
          <Space direction="vertical" size={2}>
            <Space>
              <Avatar src={user.photoURL || null}>{(user.displayName || user.email || 'U').charAt(0)}</Avatar>
              <a href={href}>{user.displayName || user.email || user.id}</a>
            </Space>
            {user.email ? (
              <Space size={6} style={{ fontSize: 12 }}>
                <MailOutlined />
                <span>{user.email}</span>
                {user.emailVerified ? (
                  <Tooltip title="Verified">
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Not verified">
                    <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  </Tooltip>
                )}
              </Space>
            ) : user.phoneNumber ? (
              <Space size={6} style={{ fontSize: 12 }}>
                <PhoneOutlined />
                <span>{user.phoneNumber}</span>
              </Space>
            ) : null}
            <Space size={6} style={{ fontSize: 12 }}>
              <CalendarOutlined />
              <span>{createdAtLabel}</span>
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'Access',
      key: 'access',
      render: (_, user) => {
        const method = formatLoginMethod(getPrimaryAuthProvider(user));
        const platform = user.platform || 'web';
        return (
          <Space direction="vertical" size={2}>
            <Space size={6}>
              <LockOutlined />
              <span>{method}</span>
            </Space>
            <Space size={6}>
              <GlobalOutlined />
              <span>{platform}</span>
            </Space>
            <Tooltip title={user.isHuman ? 'Verified human' : 'Not human-verified'}>
              <Space size={6}>
                {user.isHuman
                  ? <SmileOutlined style={{ color: '#52c41a' }} />
                  : <RobotOutlined style={{ color: '#ff4d4f' }} />}
                <span style={{ fontSize: 12 }}>
                  {user.isHuman ? 'Human' : 'Bot?'}
                </span>
              </Space>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Signup Source',
      key: 'origin',
      render: (_, user) => {
        const reason = getPrimaryOriginReason(user) || '-';
        const path = getPrimaryOriginPath(user);
        const href = path ? (path.startsWith('/') ? `${URLPath}${path}` : path) : null;
        const display = path && path.length > 42 ? `${path.slice(0, 39)}...` : path;
        return (
          <Space direction="vertical" size={2}>
            <Tag color={reason === '-' ? 'default' : 'purple'} style={{ marginRight: 0, width: 'fit-content' }}>
              {reason}
            </Tag>
            {href ? (
              <Tooltip title={path}>
                <a href={href} target="_blank" rel="noopener noreferrer">
                  <Space size={6}>
                    <LinkOutlined />
                    <span style={{ fontSize: 12 }}>{display}</span>
                  </Space>
                </a>
              </Tooltip>
            ) : (
              <span style={{ fontSize: 12, color: '#999' }}>-</span>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Client',
      key: 'clientId',
      render: (_, user) => {
        const { clientId, customerId, roles } = user;
        const roleLabels = Array.isArray(roles)
          ? roles.filter((r) => r === 'client' || r === 'customer')
          : [];
        if (!clientId && !customerId && roleLabels.length === 0) return '-';
        return (
          <Space direction="vertical" size={2}>
            {clientId && (
              <a href={`${URLPath}/admin/clients/${clientId}`}>
                <code style={{ fontSize: 11 }}>{clientId}</code>
              </a>
            )}
            {customerId && (
              <a href={`${URLPath}/admin/customers/${customerId}`}>
                <code style={{ fontSize: 11 }}>{customerId}</code>
              </a>
            )}
            {roleLabels.length > 0 && (
              <Space size={2}>
                {roleLabels.map((r) => (
                  <Tag key={r} color={r === 'customer' ? 'cyan' : 'blue'} style={{ fontSize: 11, margin: 0 }}>
                    {r}
                  </Tag>
                ))}
              </Space>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, user) => (
        <Button
          type="primary"
          size="small"
          disabled={!!user.emailVerified || !user.email}
          loading={savingUserId === user.id}
          onClick={() => resendVerificationEmail(user.id, user.email)}
        >
          Resend Email
        </Button>
      ),
    },
  ];

  return (
    <div style={{ height: '100%', textAlign: 'center' }}>
      <PageHeader
        ghost={false}
        title="Users"
        subTitle="Non-anonymous users (max 400 by date range)"
      />

      <Space style={{ width: '100%', padding: '0 12px 12px 12px' }} direction="vertical">
        <CredentialHealthCard />
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(range) => setDateRange(range && range[0] && range[1] ? range : getCurrentWeekRange())}
            onOpenChange={(open) => { if (open) setPresetRefresh((t) => t + 1); }}
            ranges={getAdminDatePresets()}
            allowClear={false}
          />
          <Select
            value={emailVerifiedMode}
            style={{ minWidth: 180 }}
            onChange={setEmailVerifiedMode}
          >
            <Select.Option value="all">Email Verified: All</Select.Option>
            <Select.Option value="true">Email Verified: true</Select.Option>
            <Select.Option value="false">Email Verified: false</Select.Option>
          </Select>
          <Input.Search
            placeholder="Search email, uid, clientId, platform..."
            allowClear
            style={{ minWidth: 320 }}
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText}
          />
          <span style={{ color: (filteredUsers || []).length >= LIMIT ? '#ff4d4f' : undefined }}>
            {(filteredUsers || []).length} result{(filteredUsers || []).length !== 1 ? 's' : ''} (max {LIMIT})
          </span>
          <Button onClick={exportUsersCsv} disabled={!filteredUsers || filteredUsers.length === 0}>
            Export CSV
          </Button>
        </Space>
      </Space>

      {loading ? (
        <CenteredSpinner loading={true} noLayout={false} />
      ) : (
        <Table
          style={{ textAlign: 'left', padding: '0 12px 12px 12px' }}
          rowKey="id"
          dataSource={filteredUsers}
          columns={columns}
          scroll={{ x: 1100 }}
          pagination={{ pageSize: 50, showSizeChanger: false }}
        />
      )}
    </div>
  );
}
